<template>
    <div class="text-left">
        <!-- Recursos do gateway -->
        <v-row v-if="gauges.length > 0" dense class="mb-2">
            <v-col v-for="gauge in gauges" :key="gauge.key" cols="6" sm="4" md="2">
                <v-card outlined class="pa-3 text-center fill-height">
                    <v-progress-circular :value="gauge.percent" :color="gauge.color" size="76" width="7" rotate="-90">
                        <span class="subtitle-2 font-weight-bold">{{ gauge.text }}</span>
                    </v-progress-circular>
                    <div class="caption mt-2"><v-icon small class="mr-1">{{ gauge.icon }}</v-icon>{{ gauge.label }}</div>
                    <div v-if="gauge.hint" :class="`caption ${gauge.color}--text`">{{ gauge.hint }}</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Rede -->
        <v-card v-if="interfaces.length > 0" outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-medium py-2">
                <v-icon left>lan</v-icon>Interfaces de rede
                <v-spacer></v-spacer>
                <span class="caption grey--text">{{ interfaces.length }} interface(s)</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-simple-table dense>
                <thead>
                    <tr>
                        <th class="text-left">Tipo</th>
                        <th class="text-left">Interface</th>
                        <th class="text-left">Endereço IP</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in interfaces" :key="item.name">
                        <td><v-icon small :color="item.color" class="mr-2">{{ item.icon }}</v-icon>{{ item.label }}</td>
                        <td><code>{{ item.name }}</code></td>
                        <td>
                            <strong v-if="item.ip" class="text-no-wrap">{{ item.ip }}</strong>
                            <span v-else class="grey--text">Sem endereço</span>
                        </td>
                        <td class="text-right">
                            <v-btn v-if="item.ip && canCopy" icon x-small title="Copiar IP" @click="copy(item.ip)"><v-icon x-small>content_copy</v-icon></v-btn>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-card>

        <!-- Carregadores configurados no gateway -->
        <v-card v-for="charger in chargers" :key="charger.uuid" outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-medium py-2">
                <v-icon left>ev_station</v-icon>{{ charger.title }}
                <v-spacer></v-spacer>
                <v-chip v-if="charger.policy" small label outlined class="ml-1">{{ charger.policy }}</v-chip>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text>
                <div class="caption grey--text mb-3">{{ charger.uuid }}</div>

                <v-row dense>
                    <v-col v-for="field in charger.fields" :key="field.label" cols="6" sm="4" md="3">
                        <div class="caption grey--text">{{ field.label }}</div>
                        <div class="body-2 font-weight-medium text-truncate" :title="field.value">{{ field.value }}</div>
                    </v-col>
                </v-row>

                <template v-if="charger.slaves.length > 0">
                    <div class="overline mt-3">Placas na RS-485</div>
                    <v-row dense>
                        <v-col v-for="slave in charger.slaves" :key="slave.slaveId" cols="12" sm="6" md="4">
                            <v-card outlined :class="`pa-2 slave slave--${slave.status.color}`">
                                <div class="d-flex align-center">
                                    <v-icon :color="slave.status.color" class="mr-2">{{ slave.status.icon }}</v-icon>
                                    <div class="flex-grow-1" style="min-width: 0">
                                        <div class="body-2 font-weight-medium text-truncate" :title="slave.names.join(', ')">{{ slave.names.join(", ") }}</div>
                                        <div class="caption grey--text">Endereço {{ slave.slaveId }} · {{ slave.status.text }}</div>
                                    </div>
                                </div>
                            </v-card>
                        </v-col>
                    </v-row>
                </template>

                <template v-if="charger.ocpp">
                    <div class="overline mt-3">OCPP {{ charger.ocpp.version }}</div>
                    <div class="body-2 text-truncate" :title="charger.ocpp.address"><v-icon small class="mr-1">link</v-icon>{{ charger.ocpp.address }}</div>
                </template>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
// Os campos de info dependem da versão do gateway, cada bloco só é exibido quando existe
const toNumber = (value) => (value === "" || value === null || value === undefined || isNaN(Number(value)) ? null : Number(value));

const level = (value, warning, critical) => (value > critical ? "red" : value > warning ? "orange" : "green");

// Tipo da interface pelo prefixo do nome no Linux, na ordem de exibição
const INTERFACE_TYPES = [
    { prefix: /^(eth|en)/, label: "Ethernet", icon: "settings_ethernet", color: "primary" },
    { prefix: /^(wlan|wl)/, label: "Wi-Fi", icon: "wifi", color: "primary" },
    { prefix: /^(ppp|wwan|usb)/, label: "Celular", icon: "signal_cellular_alt", color: "primary" },
    { prefix: /^(tun|tap|wg)/, label: "VPN", icon: "vpn_lock", color: "green" },
];
const INTERFACE_OTHER = { label: "Outra", icon: "lan", color: "grey" };

export default {
    props: {
        info: {
            type: Object,
            required: true,
        },
    },

    computed: {
        gauges() {
            const info = this.info;
            const gauges = [];
            const percent = (key, label, icon, warning, critical, hint) => {
                const value = toNumber(info[key]);
                if (value !== null) {
                    const color = level(value, warning, critical);
                    gauges.push({ key, label, icon, percent: value, text: `${Math.round(value)}%`, color, hint: color != "green" ? hint : null });
                }
            };

            const temperature = toNumber(info.temperature);
            if (temperature !== null) {
                const celsius = temperature / 1000;
                const color = level(temperature, 75000, 82000);
                // Escala até 85 ºC, temperatura em que o gateway para
                gauges.push({ key: "temperature", label: "Temperatura", icon: "thermostat", percent: (celsius / 85) * 100, text: `${Math.round(celsius)}ºC`, color, hint: color != "green" ? "Para em 85 ºC" : null });
            }
            percent("cpu", "CPU", "speed", 70, 90, "Uso elevado");
            percent("memory", "RAM", "memory", 75, 90, "Uso elevado");
            percent("flash", "Flash", "sd_storage", 75, 90, "Pouco espaço");
            percent("bufferAlocation", "Backup", "backup", 90, 92, "Aguardando formatação");
            percent("configurationAlocation", "Configuração", "settings", 60, 70, "Reporte ao suporte");
            return gauges;
        },
        interfaces() {
            const network = this.info.network || {};
            const order = (name) => {
                const index = INTERFACE_TYPES.findIndex((type) => type.prefix.test(name));
                return index < 0 ? INTERFACE_TYPES.length : index;
            };
            return Object.keys(network)
                .filter((name) => name != "lo")
                .sort((a, b) => order(a) - order(b) || a.localeCompare(b, undefined, { numeric: true }))
                .map((name) => {
                    const { label, icon, color } = INTERFACE_TYPES.find((type) => type.prefix.test(name)) || INTERFACE_OTHER;
                    return { name, ip: network[name], label, icon, color };
                });
        },
        canCopy() {
            return !!(navigator.clipboard && window.isSecureContext);
        },
        // Estatística da última janela RS-485 por carregador e endereço
        rs485() {
            const map = {};
            (this.info.diagnostic || []).forEach((diagnostic) => {
                ((diagnostic.rs485 && diagnostic.rs485.devices) || []).forEach((device) => {
                    map[`${device.uuid}/${device.slaveId}`] = device;
                });
            });
            return map;
        },
        chargers() {
            const chargers = this.info.chargers || {};
            return Object.keys(chargers).map((uuid) => {
                const charger = chargers[uuid] || {};

                const fields = [];
                const addField = (label, value) => {
                    if (value !== undefined && value !== null && value !== "") fields.push({ label, value });
                };
                addField("Modelo", charger.link_name ? charger.name : null);
                addField("Corrente máxima", toNumber(charger.max_current) !== null ? `${charger.max_current} A` : null);
                addField("Histórico", toNumber(charger.history_time) !== null ? this.duration(Number(charger.history_time)) : null);
                addField("Servidor", charger.server_id);

                // Várias placas podem compartilhar o mesmo endereço (ex.: medidores do mesmo equipamento)
                const slaves = {};
                Object.keys(charger.boards || {}).forEach((name) => {
                    const slaveId = charger.boards[name];
                    if (!slaves[slaveId]) slaves[slaveId] = [];
                    slaves[slaveId].push(name);
                });

                let ocpp = null;
                if (charger.ocpp && (charger.ocpp.uri || charger.ocpp.ip)) {
                    const { uri, ip, port, version } = charger.ocpp;
                    ocpp = { version: version || "", address: uri || (port ? `${ip}:${port}` : ip) };
                }

                return {
                    uuid,
                    title: charger.link_name || charger.name || uuid,
                    policy: charger.policy,
                    fields,
                    ocpp,
                    slaves: Object.keys(slaves)
                        .sort((a, b) => a - b)
                        .map((slaveId) => ({ slaveId, names: slaves[slaveId].sort(), status: this.slaveStatus(this.rs485[`${uuid}/${slaveId}`]) })),
                };
            });
        },
    },

    methods: {
        copy(text) {
            navigator.clipboard
                .writeText(text)
                .then(() => this.$eventHub.$emit("msgSuccess", `IP ${text} copiado.`))
                .catch(() => {});
        },
        duration(seconds) {
            if (seconds % 3600 == 0) return `${seconds / 3600} h`;
            if (seconds % 60 == 0) return `${seconds / 60} min`;
            return `${seconds} s`;
        },
        slaveStatus(device) {
            if (!device || !device.lastWindow || !device.lastWindow.reads) {
                return { color: "grey", icon: "help_outline", text: "Sem estatística" };
            }
            const { reads, ok } = device.lastWindow;
            const errors = reads - ok;
            if (ok == 0) {
                return { color: "red", icon: "link_off", text: `Sem resposta (${reads} leituras)` };
            }
            if (errors > 0) {
                const percent = ((errors / reads) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
                return { color: "orange", icon: "warning", text: `${percent}% de erros (${errors} de ${reads})` };
            }
            return { color: "green", icon: "check_circle", text: `Sem erros (${reads} leituras)` };
        },
    },
};
</script>

<style scoped>
.slave {
    border-left-width: 4px !important;
}
.slave--green {
    border-left-color: #4caf50 !important;
}
.slave--orange {
    border-left-color: #ff9800 !important;
}
.slave--red {
    border-left-color: #f44336 !important;
}
.slave--grey {
    border-left-color: #9e9e9e !important;
}
</style>
